// Sales Dashboard v2 — Reports section. The classic Sales Dashboard's report
// tabs (Top SKUs / Customers / Collections / Sizes / Colors / Styles /
// Warehouses / Zip Codes / Items by Zip) rebuilt on the ClickHouse warehouse:
// every tab is one server-side aggregation via /api/SalesDashboardV2/report,
// so it stays fast no matter the date range. View modes: table, bar chart,
// pie, and US map (warehouses + zips). Compare-mode adds a "vs Prev" column.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Chip, Button, IconButton, TextField, MenuItem,
  Select, Table, TableHead, TableBody, TableRow, TableCell, ToggleButton,
  ToggleButtonGroup, Tooltip as MuiTooltip, Skeleton, Alert, Menu,
  InputAdornment, CircularProgress, Avatar, Dialog,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  MdFileDownload, MdContentCopy, MdSearch, MdArrowDropDown, MdInventory2,
  MdAttachMoney, MdFormatListBulleted, MdVisibility, MdTableChart,
  MdBarChart, MdPieChart, MdMap, MdAdd, MdRemove, MdCenterFocusStrong,
  MdShowChart, MdFullscreen, MdFullscreenExit,
} from 'react-icons/md';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, LabelList, LineChart, Line as RLine,
} from 'recharts';
import { PERIOD_COLORS } from './SalesDashboardV2Compare';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup, Line } from 'react-simple-maps';
import { geoAlbersUsa } from 'd3-geo';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

const US_TOPO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Curated warehouse pin coordinates (nudged to avoid overlap) — same as the
// classic dashboard. Codes missing here still appear in the table below the map.
const WAREHOUSE_LOCATIONS = {
  L10: { name: 'Port Washington, NY', lat: 41.4, lng: -72.5, region: 'Northeast' },
  L21: { name: 'Easton, PA', lat: 41.8, lng: -75.2, region: 'Northeast' },
  L28: { name: 'Easton, PA', lat: 40.0, lng: -75.8, region: 'Northeast' },
  L29: { name: 'Carlisle, PA', lat: 39.2, lng: -77.6, region: 'Northeast' },
  L40: { name: 'Lebanon, NJ', lat: 42.3, lng: -73.5, region: 'Northeast' },
  L41: { name: 'Flemington, NJ', lat: 40.7, lng: -73.2, region: 'Northeast' },
  L35: { name: 'Savannah, GA', lat: 33.0, lng: -82.0, region: 'Southeast' },
  L36: { name: 'Port Wentworth, GA', lat: 31.5, lng: -82.5, region: 'Southeast' },
  L37: { name: 'Midway, GA', lat: 32.2, lng: -80.2, region: 'Southeast' },
  L55: { name: 'Whitestown, IN', lat: 40.8, lng: -86.0, region: 'Midwest' },
  L56: { name: 'Whitestown, IN', lat: 39.2, lng: -86.8, region: 'Midwest' },
  L57: { name: 'Joliet, IL', lat: 41.5, lng: -88.1, region: 'Midwest' },
  L60: { name: 'Riverside, CA', lat: 33.9, lng: -117.4, region: 'West' },
  L65: { name: 'Patterson, CA', lat: 37.5, lng: -121.1, region: 'West' },
  L70: { name: 'Baytown, TX', lat: 29.7, lng: -95.0, region: 'Southwest' },
  L91: { name: 'Morrisburg, ON (Canada)', lat: 44.9, lng: -75.19, region: 'Northeast' },
};

const REGION_COLORS = {
  Northeast: '#7C6F8A', Southeast: '#6B8F71', Midwest: '#8B7355',
  West: '#5B8FA6', Southwest: '#A67B5B', Central: '#888',
};

// NOTE: geoAlbersUsa cannot project points outside the lower-48 + AK/HI insets;
// an unprojectable Marker crashes react-simple-maps. PR is intentionally absent
// (it still shows in the tables), and the render also guards unknown coords.
const STATE_COORDS = {
  AL:[32.8,-86.8],AK:[64.2,-152.5],AZ:[34.0,-111.1],AR:[35.2,-91.8],CA:[36.8,-119.4],
  CO:[39.1,-105.4],CT:[41.6,-72.7],DE:[39.0,-75.5],FL:[27.8,-81.7],GA:[32.2,-83.1],
  HI:[19.9,-155.6],ID:[44.1,-114.7],IL:[40.3,-89.0],IN:[40.3,-86.1],IA:[42.0,-93.2],
  KS:[38.5,-98.3],KY:[37.8,-84.3],LA:[30.9,-92.3],ME:[45.3,-69.4],MD:[39.0,-76.6],
  MA:[42.4,-71.4],MI:[44.3,-85.6],MN:[46.7,-94.7],MS:[32.7,-89.7],MO:[38.5,-91.8],
  MT:[46.8,-110.4],NE:[41.5,-99.9],NV:[38.8,-116.4],NH:[43.2,-71.6],NJ:[40.1,-74.5],
  NM:[34.5,-105.9],NY:[43.0,-75.0],NC:[35.8,-80.8],ND:[47.5,-100.5],OH:[40.4,-82.9],
  OK:[35.0,-97.1],OR:[43.8,-120.6],PA:[41.2,-77.2],RI:[41.6,-71.5],SC:[33.8,-81.2],
  SD:[43.9,-99.4],TN:[35.5,-86.6],TX:[31.0,-100.0],UT:[39.3,-111.1],VT:[44.6,-72.6],
  VA:[37.4,-79.5],WA:[47.8,-120.7],WV:[38.5,-80.5],WI:[43.8,-89.5],WY:[43.1,-107.6],
  DC:[38.9,-77.0],
};

const STATE_NAME_TO_ABBREV = {Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',Wisconsin:'WI',Wyoming:'WY','District of Columbia':'DC'};

const PIE_COLORS = [
  '#0072B2', '#E69F00', '#009E73', '#CC79A7', '#56B4E9',
  '#D55E00', '#8B7355', '#5B8FA6', '#A67B5B', '#999999',
];

const TOP_N_OPTIONS = [
  { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 100, label: '100' },
  { value: 250, label: '250' }, { value: 500, label: '500' }, { value: 0, label: 'All' },
];

const fmtDollar = (v) => '$' + (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v) => (Number(v) || 0).toLocaleString('en-US');
// Compact $ for bar-end labels so they don't overflow (e.g. $1.2M, $340k).
const fmtDollarShort = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
  if (n >= 1e3) return '$' + Math.round(n / 1e3) + 'k';
  return '$' + Math.round(n);
};

// ── Color-family swatches ────────────────────────────────────────────────────
// Static name→hex lookup so the Colors tab can show a chip matching each color
// name. Pure in-memory map — no network, no per-row cost, instant. Light tones
// carry an inset border so they read on a light background; unknown names get a
// neutral hatch and multi/assorted a small spectrum.
const COLOR_SWATCHES = {
  offwhite:'#F1EDE3', white:'#FCFBF8', ivory:'#F1EADA', cream:'#F2E7CF', bone:'#ECE5D6', linen:'#EFE7D8',
  beige:'#D7C39E', tan:'#D2B48C', taupe:'#B7A78D', sand:'#DAC7A3', khaki:'#C3B091', camel:'#C19A6B',
  natural:'#E2D5BC', unfinished:'#C8A46D', wood:'#B4875A', oak:'#C8A870', walnut:'#5C4433',
  gray:'#9AA0A6', grey:'#9AA0A6', silver:'#C6CBD2', charcoal:'#3A3D43', slate:'#5A6570', pewter:'#8E9196',
  black:'#1C1C1E', ebony:'#242423',
  brown:'#6E4B30', chocolate:'#472C1B', espresso:'#3A2417', rust:'#A6431F', copper:'#B4693B', terracotta:'#C56A4E', mocha:'#7C5A44',
  blue:'#2F6FB0', navy:'#20365E', teal:'#1B8A8A', turquoise:'#1FA6A6', denim:'#3E5C79', aqua:'#3FBFBF', indigo:'#3F4C8C', cobalt:'#245BB5',
  green:'#4C8C4A', olive:'#7A7B33', sage:'#9CAF88', mint:'#9AD1B0', forest:'#2E5D3A', emerald:'#2E8B57', lime:'#A4C639',
  red:'#C0392B', burgundy:'#6E1F2A', wine:'#6E1F2A', maroon:'#6E1F2A', crimson:'#B01B2E', brick:'#9C4A3C',
  orange:'#E07B2F', coral:'#E5765C', peach:'#F0B891', apricot:'#EBA765', pumpkin:'#D96C2C',
  gold:'#C79A3A', yellow:'#E6C34A', mustard:'#C9A227', amber:'#D99A2B', honey:'#D6A94E',
  pink:'#E68FA6', blush:'#E7C0C8', rose:'#D96B85', fuchsia:'#C33C82', magenta:'#B5327E', mauve:'#B784A7',
  purple:'#7E57C2', lavender:'#B7A6DE', plum:'#6E4A73', violet:'#8B5CC0', lilac:'#C4A9DE', eggplant:'#4B2E4A',
  bronze:'#8C6B3F', brass:'#B08D57', nickel:'#B7BBC0', chrome:'#CFD3D6', gunmetal:'#4A4E54',
};
const MULTI_COLORS = new Set(['multi', 'multicolor', 'multicolour', 'multicolored', 'assorted', 'rainbow', 'mixed', 'various']);
const normColor = (s) => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
let _cssColorOk = null; // cache CSS.supports availability once
const isCssColorName = (name) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n || /[^a-z]/.test(n)) return false; // single plain word only
  if (_cssColorOk === null) _cssColorOk = !!(typeof window !== 'undefined' && window.CSS && window.CSS.supports);
  return _cssColorOk ? window.CSS.supports('color', n) : false;
};
// Resolve ONE color word/phrase (no separators) to a hex, or null.
function resolveOneColor(part) {
  const key = normColor(part);
  if (!key) return null;
  if (COLOR_SWATCHES[key]) return COLOR_SWATCHES[key];
  if (isCssColorName(part)) return String(part).trim().toLowerCase();
  // compound name ("dark blue", "sea green") → longest contained known token
  const hit = Object.keys(COLOR_SWATCHES)
    .filter((k) => k.length > 2 && key.includes(k))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? COLOR_SWATCHES[hit] : null;
}
// → { type:'solid', color } | { type:'split', colors:[…] } | { type:'multi' } | { type:'none' }
// Names like "BLUE / GREY", "IVORY & BLACK", "RUST, NATURAL" split into one
// segment per part so the chip shows every color, not just the first.
function swatchStyle(name) {
  const raw = String(name || '').trim();
  if (!raw) return { type: 'none' };
  if (MULTI_COLORS.has(normColor(raw))) return { type: 'multi' };
  const parts = raw.split(/[/&,+]/).map((p) => p.trim()).filter(Boolean);
  if (parts.length > 1) {
    const colors = parts.map(resolveOneColor).filter(Boolean);
    if (colors.length > 1) return { type: 'split', colors };
    if (colors.length === 1) return { type: 'solid', color: colors[0] };
    return { type: 'none' };
  }
  const c = resolveOneColor(raw);
  return c ? { type: 'solid', color: c } : { type: 'none' };
}
// Fill for a color-tab data point (bar/pie); null → fall back to theme accent.
// Split names tint by their first (dominant) color.
const colorFill = (name) => {
  const s = swatchStyle(name);
  return s.type === 'solid' ? s.color : s.type === 'split' ? s.colors[0] : null;
};
// Hard-stop gradient: one equal segment per color ("BLUE / GREY" → half blue, half grey).
const splitBg = (colors) => {
  const seg = 100 / colors.length;
  return `linear-gradient(90deg, ${colors.map((c, i) => `${c} ${(i * seg).toFixed(1)}% ${((i + 1) * seg).toFixed(1)}%`).join(', ')})`;
};

// Product thumbnail for the Top SKUs table (mirrors the Overview list). Lazy
// image, icon fallback when the SKU has no primary image, larger hover preview.
function SkuThumb({ src, sku }) {
  const body = (
    <Avatar variant="rounded" src={src || undefined} imgProps={{ loading: 'lazy' }}
      sx={{ width: 34, height: 34, bgcolor: 'action.hover', mx: 'auto' }}>
      <MdInventory2 size={16} style={{ opacity: 0.4 }} />
    </Avatar>
  );
  if (!src) return body;
  return (
    <MuiTooltip arrow placement="right" title={
      <Box sx={{ textAlign: 'center' }}>
        <Box component="img" src={src} alt={sku || ''} loading="lazy"
          sx={{ width: 150, height: 150, objectFit: 'contain', display: 'block', borderRadius: 1, bgcolor: '#fff' }} />
        {sku ? <Typography sx={{ fontSize: 10.5, mt: 0.5, opacity: 0.85 }}>{sku}</Typography> : null}
      </Box>
    }>
      {body}
    </MuiTooltip>
  );
}
function ColorSwatch({ name }) {
  const s = swatchStyle(name);
  const bg = s.type === 'multi'
    ? 'conic-gradient(#e53935,#fb8c00,#fdd835,#43a047,#1e88e5,#8e24aa,#e53935)'
    : s.type === 'split' ? splitBg(s.colors)
    : s.type === 'solid' ? s.color
    : 'repeating-linear-gradient(45deg,#c9c9c9 0 3px,#e9e9e9 3px 6px)';
  const caption = s.type === 'none' ? 'no swatch'
    : s.type === 'multi' ? 'multicolor'
    : s.type === 'split' ? s.colors.join(' + ') : s.color;
  // hover = large preview panel (same pattern as the SKU image preview)
  return (
    <MuiTooltip arrow placement="right" title={
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{
          width: 150, height: 150, borderRadius: 1.5, background: bg,
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)',
        }} />
        <Typography sx={{ fontSize: 10.5, mt: 0.5, opacity: 0.85 }}>{name || '—'} · {caption}</Typography>
      </Box>
    }>
      <Box component="span" sx={{
        width: 22, height: 22, borderRadius: '5px', display: 'inline-block', verticalAlign: 'middle',
        background: bg, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)',
      }} />
    </MuiTooltip>
  );
}

// Tab registry — the parent renders these labels after its Overview tab.
export const REPORT_TABS = [
  { key: 'sku', label: 'Top SKUs', title: 'Top Selling SKUs', file: 'Top_SKUs' },
  { key: 'customer', label: 'Top Customers', title: 'Top Selling Customers', file: 'Top_Customers' },
  { key: 'collection', label: 'Collections', title: 'Top Selling Collections', file: 'Top_Collections' },
  { key: 'size', label: 'Sizes', title: 'Top Selling Sizes', file: 'Top_Sizes' },
  { key: 'color', label: 'Colors', title: 'Top Selling Colors', file: 'Top_Colors' },
  { key: 'style', label: 'Styles', title: 'Top Selling Styles', file: 'Top_Styles' },
  { key: 'warehouse', label: 'Warehouses', title: 'Top Shipping Warehouses', file: 'Top_Warehouses' },
  { key: 'zip', label: 'Zip Codes', title: 'Top Shipping to Zip Code', file: 'Top_ZipCodes' },
  { key: 'zipitem', label: 'Items by Zip', title: 'Top Items Shipped by Zip Code', file: 'Items_By_Zip' },
];

// dims whose Graph view supports per-entity lines (single-key dims — the
// backend's report-series rejects composite-key dims like zip/zipitem)
const SERIES_DIMS = new Set(['sku', 'customer', 'collection', 'size', 'color', 'style', 'warehouse']);

// Fullscreen wrapper for any chart: the SAME chart element re-renders inside a
// fullscreen dialog (ResponsiveContainer re-flows to the new parent size).
export function ChartFullscreen({ open, onClose, title, children }) {
  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1 }} noWrap>{title}</Typography>
        <MuiTooltip title="Exit full screen (Esc)">
          <IconButton onClick={onClose}><MdFullscreenExit size={22} /></IconButton>
        </MuiTooltip>
      </Box>
      <Box sx={{ height: 'calc(100vh - 58px)', p: { xs: 1, md: 2.5 } }}>{children}</Box>
    </Dialog>
  );
}
export const FsButton = ({ onClick }) => (
  <MuiTooltip title="Full screen">
    <IconButton size="small" onClick={onClick} sx={{ ml: 'auto' }}><MdFullscreen size={18} /></IconButton>
  </MuiTooltip>
);

// ---- vs-Prev helpers ---------------------------------------------------------
const changeOf = (r) => {
  const prev = Number(r.prevRevenue) || 0;
  if (!prev) return null; // absent (or zero) in the previous window → NEW
  return (((Number(r.revenue) || 0) - prev) / Math.abs(prev)) * 100;
};
const changeCell = (r) => {
  const c = changeOf(r);
  return c === null ? 'NEW' : `${c >= 0 ? '+' : ''}${c.toFixed(1)}%`;
};
const changeRaw = (r) => {
  const c = changeOf(r);
  return c === null ? Number.POSITIVE_INFINITY : c;
};

// Column definitions per tab. raw() feeds sorting; cell() feeds display/export.
function getColumns(tabKey, ctx) {
  // Multi-period columns ($ + Δ% per selected period) ride in the same slot as
  // the legacy vs-Prev column, so every tab case picks them up unchanged.
  // When periods are active they replace compare=1 (the fetch omits it too).
  const perCols = (ctx.periods || []).flatMap((p, k) => {
    const prevOf = (r) => Number(r[`pRevenue_${k}`]) || 0;
    return [
      { label: `$ · ${p.label}`, align: 'right', sortable: true, raw: prevOf, cell: (r) => fmtDollar(prevOf(r)) },
      {
        label: `Δ% · ${p.label}`, align: 'right', sortable: true, isChange: true,
        raw: (r) => { const pv = prevOf(r); return pv ? (((Number(r.revenue) || 0) - pv) / Math.abs(pv)) * 100 : Number.POSITIVE_INFINITY; },
        cell: (r) => {
          const pv = prevOf(r);
          if (!pv) return 'NEW';
          const c = (((Number(r.revenue) || 0) - pv) / Math.abs(pv)) * 100;
          return `${c >= 0 ? '+' : ''}${c.toFixed(1)}%`;
        },
      },
    ];
  });
  const vsPrev = perCols.length ? perCols : (ctx.compare
    ? [{ label: 'vs Prev', align: 'right', sortable: true, raw: changeRaw, cell: changeCell, isChange: true }]
    : []);
  switch (tabKey) {
    case 'sku':
      return [
        { label: 'SKU (Item ID)', align: 'left', cell: (r) => r.x || '' },
        // Product thumbnail (thumbs=1 on the report fetch) — display-only, like
        // the Overview's Top SKUs list. Lazy-loaded so a 500-row table doesn't
        // fetch every image up front.
        { label: 'Image', align: 'center', noExport: true, cell: () => '', render: (r) => <SkuThumb src={r.thumb} sku={r.x} /> },
        { label: 'Collection', align: 'left', cell: (r) => r.collection || '' },
        { label: 'Qty', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Units/Day', align: 'right', sortable: true, raw: (r) => (Number(r.units) || 0) / ctx.days, cell: (r) => ((Number(r.units) || 0) / ctx.days).toFixed(1) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
        ...vsPrev,
      ];
    case 'customer':
      return [
        { label: 'Customer', align: 'left', cell: (r) => r.x || '' },
        { label: 'Orders', align: 'right', sortable: true, raw: (r) => Number(r.orders) || 0, cell: (r) => fmtNum(r.orders) },
        { label: 'Units', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
        ...vsPrev,
      ];
    case 'warehouse':
      return [
        { label: 'WHS', align: 'left', cell: (r) => r.x || '' },
        { label: 'Location', align: 'left', cell: (r) => WAREHOUSE_LOCATIONS[r.x]?.name || r.label || '' },
        { label: 'Orders', align: 'right', sortable: true, raw: (r) => Number(r.orders) || 0, cell: (r) => fmtNum(r.orders) },
        { label: 'Qty', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
        { label: 'Avg Order $', align: 'right', sortable: true, raw: (r) => (Number(r.orders) ? (Number(r.revenue) || 0) / Number(r.orders) : 0), cell: (r) => fmtDollar(Number(r.orders) ? (Number(r.revenue) || 0) / Number(r.orders) : 0) },
        { label: 'Items/Order', align: 'right', sortable: true, raw: (r) => (Number(r.orders) ? (Number(r.units) || 0) / Number(r.orders) : 0), cell: (r) => (Number(r.orders) ? ((Number(r.units) || 0) / Number(r.orders)).toFixed(1) : '0.0') },
        { label: 'Orders/Day', align: 'right', sortable: true, raw: (r) => (Number(r.orders) || 0) / ctx.days, cell: (r) => ((Number(r.orders) || 0) / ctx.days).toFixed(1) },
        ...vsPrev,
      ];
    case 'zip':
      return [
        { label: 'Zip Code (State)', align: 'left', cell: (r) => `${r.x}${r.state ? ` (${r.state})` : ''}` },
        { label: 'Orders', align: 'right', sortable: true, raw: (r) => Number(r.orders) || 0, cell: (r) => fmtNum(r.orders) },
        { label: 'Quantity', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
        ...vsPrev,
      ];
    case 'zipitem':
      return [
        { label: 'Zip Code (State)', align: 'left', cell: (r) => `${r.x}${r.state ? ` (${r.state})` : ''}` },
        { label: 'Item ID', align: 'left', cell: (r) => r.item || '' },
        { label: 'Collection', align: 'left', cell: (r) => r.collection || '' },
        { label: 'Quantity', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
      ];
    default: {
      // collection / size / color / style
      const labels = { collection: 'Collection', size: 'Size', color: 'Color', style: 'Style' };
      // Name stays at index 0 (chart/pie labels read columns[0]).
      const cols = [{ label: labels[tabKey] || 'Name', align: 'left', cell: (r) => r.x || '' }];
      // Colors tab: a swatch chip matching the color name (display-only, no export).
      if (tabKey === 'color') {
        cols.push({ label: 'Swatch', align: 'center', noExport: true, cell: () => '', render: (r) => <ColorSwatch name={r.x} /> });
      }
      // Collections tab: the collection's top-selling item's image (thumbs=1),
      // server-side fallback to any product image in the collection.
      if (tabKey === 'collection') {
        cols.push({ label: 'Image', align: 'center', noExport: true, cell: () => '', render: (r) => <SkuThumb src={r.thumb} sku={r.thumbItem || r.x} /> });
      }
      cols.push(
        { label: 'Quantity', align: 'right', sortable: true, raw: (r) => Number(r.units) || 0, cell: (r) => fmtNum(r.units) },
        { label: 'Amount', align: 'right', sortable: true, raw: (r) => Number(r.revenue) || 0, cell: (r) => fmtDollar(r.revenue) },
        ...vsPrev,
      );
      return cols;
    }
  }
}

const SUBTITLES = {
  sku: (ctx) => `Sorted by Amount • Velocity = units/day over ${ctx.days} days${ctx.compare ? ' • vs Previous Period' : ''}`,
  customer: (ctx) => (ctx.compare ? 'Sorted by Amount • vs Previous Period' : 'Sorted by Amount ($$)'),
  collection: () => 'Sorted by Amount ($$)',
  size: () => 'Sorted by Amount ($$) — Excludes 1x1 swatches',
  color: () => 'Sorted by Amount ($$)',
  style: () => 'Sorted by Amount ($$)',
  warehouse: (ctx) => `Efficiency metrics over ${ctx.days} days${ctx.compare ? ' • vs Previous Period' : ''}`,
  zip: (ctx) => (ctx.stateFilter ? `Filtered to ${ctx.stateFilter} — click a state on the map to change` : 'Includes State — Sorted by Amount ($$)'),
  zipitem: () => 'Sorted by Amount ($$)',
};

// ---- map zoom: buttons instead of scroll-wheel -------------------------------
// Scrolling the page must never zoom the map, so wheel events are filtered out
// of the d3-zoom behaviour; +/− buttons (and drag-to-pan) drive it instead.
const US_CENTER = [-96.5, 38.5];
const MAP_HOME = { coordinates: US_CENTER, zoom: 1 };
const wheelFilter = (evt) => evt.type !== 'wheel';

const MapZoomControls = ({ pos, setPos, dark }) => {
  const btn = {
    bgcolor: dark ? 'rgba(22,22,36,0.9)' : 'rgba(255,255,255,0.95)',
    border: '1px solid', borderColor: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
    borderRadius: 1.5, width: 30, height: 30,
    '&:hover': { bgcolor: dark ? 'rgba(40,40,60,0.95)' : '#fff' },
  };
  return (
    <Box sx={{ position: 'absolute', right: 10, bottom: 10, display: 'flex', flexDirection: 'column', gap: 0.5, zIndex: 5 }}>
      <MuiTooltip title="Zoom in" placement="left">
        <IconButton size="small" sx={btn} onClick={() => setPos((p) => ({ ...p, zoom: Math.min(12, p.zoom * 1.6) }))}><MdAdd size={16} /></IconButton>
      </MuiTooltip>
      <MuiTooltip title="Zoom out" placement="left">
        <IconButton size="small" sx={btn} onClick={() => setPos((p) => ({ ...p, zoom: Math.max(1, p.zoom / 1.6) }))}><MdRemove size={16} /></IconButton>
      </MuiTooltip>
      {(pos.zoom > 1.01 || pos.coordinates !== US_CENTER) && (
        <MuiTooltip title="Reset view" placement="left">
          <IconButton size="small" sx={btn} onClick={() => setPos(MAP_HOME)}><MdCenterFocusStrong size={16} /></IconButton>
        </MuiTooltip>
      )}
    </Box>
  );
};

const MapTooltipBox = ({ data, position }) => {
  if (!data || !position) return null;
  return (
    <Box sx={{
      position: 'fixed', left: position.x + 12, top: position.y - 10, zIndex: 9999,
      bgcolor: 'rgba(20,20,30,0.95)', color: '#fff', borderRadius: 1.5, px: 1.5, py: 1,
      pointerEvents: 'none', minWidth: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.15)',
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.3, color: '#e0d4f0' }}>{data.title}</Typography>
      {data.subtitle && <Typography sx={{ fontSize: 11, color: '#aaa', mb: 0.5 }}>{data.subtitle}</Typography>}
      {(data.rows || []).map((row, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography sx={{ fontSize: 11, color: '#bbb' }}>{row.label}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: row.color || '#fff' }}>{row.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const SummaryCard = ({ label, value, icon: Icon, theme }) => (
  <Paper elevation={0} sx={{
    borderRadius: 2, p: { xs: '10px 12px', md: '14px 20px' }, minWidth: 0,
    border: `1px solid ${theme.palette.divider}`,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{label}</Typography>
      <Icon size={18} color={theme.palette.primary.main} style={{ opacity: 0.7, flexShrink: 0 }} />
    </Box>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'primary.main', mt: 0.5, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }} noWrap>{value}</Typography>
  </Paper>
);

// =============================================================================
export default function ReportsSection({ tabKey, qs, compare, rangeDays, dropship, onDrillState, stateFilter, cmpPeriods = [], range }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [topN, setTopN] = useState(100);
  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState('');
  const [sortCol, setSortCol] = useState(-1);
  const [sortDir, setSortDir] = useState('desc');
  const [viewMode, setViewMode] = useState('table');
  const [selectedWh, setSelectedWh] = useState(() => new Set());
  const [lastSel, setLastSel] = useState('');      // most-recently selected warehouse (footprint focus)
  const [whStates, setWhStates] = useState([]);    // ship-to revenue by state (network or focused warehouse)
  const [stateRows, setStateRows] = useState([]);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [mapTooltip, setMapTooltip] = useState(null);
  const [mapTooltipPos, setMapTooltipPos] = useState(null);
  const [mapPos, setMapPos] = useState(MAP_HOME);   // controlled zoom/pan (buttons, not scroll)
  // Graph view — two modes:
  //  'top'   one line per top-N entity of this tab's dimension (styles,
  //          collections, …) over time
  //  'total' filtered total revenue over time, overlaid with the global
  //          comparison periods
  const [graphSeries, setGraphSeries] = useState(null); // total mode data
  const [entSeries, setEntSeries] = useState(null);     // top mode data {grain, rows}
  const [graphMode, setGraphMode] = useState('top');
  const [graphTop, setGraphTop] = useState(5);
  const [fsView, setFsView] = useState(false);      // fullscreen chart dialog
  const abortRef = useRef(null);

  const cmpKey = useMemo(() => cmpPeriods.map((p) => `${p.from}:${p.to}`).join(','), [cmpPeriods]);
  const mapAllowed = tabKey === 'warehouse' || tabKey === 'zip';
  const tabDef = REPORT_TABS.find((t) => t.key === tabKey) || REPORT_TABS[0];
  const ctx = useMemo(() => ({
    days: Number.isFinite(rangeDays) && rangeDays > 0 ? rangeDays : 1,
    compare, stateFilter, periods: cmpPeriods,
  }), [rangeDays, compare, stateFilter, cmpPeriods]);
  const columns = useMemo(() => getColumns(tabKey, ctx), [tabKey, ctx]);

  // reset per-tab UI state when switching tabs — including the previous tab's
  // rows/totals, so its data never renders under the new tab's headers
  useEffect(() => {
    setSearchText(''); setQ(''); setSortCol(-1); setSortDir('desc');
    setViewMode('table'); setSelectedWh(new Set()); setLastSel('');
    setRows([]); setTotals(null); setStateRows([]); setWhStates([]);
    setMapPos(MAP_HOME); setGraphSeries(null); setEntSeries(null);
    setGraphMode(SERIES_DIMS.has(tabKey) ? 'top' : 'total'); setGraphTop(5);
  }, [tabKey]);

  // fresh view when entering/leaving the map; drop fullscreen on view switch
  useEffect(() => { setMapPos(MAP_HOME); setFsView(false); }, [viewMode]);

  // Graph view: revenue over time within the active filters — the current
  // period plus one series per global comparison period, aligned by index
  // (day 1 over day 1) so different calendar windows share one x-axis.
  useEffect(() => {
    if (viewMode !== 'graph' || graphMode !== 'total') return undefined;
    let live = true;
    const grain = (Number.isFinite(rangeDays) && rangeDays > 190) ? 'month' : 'day';
    setGraphSeries(null);
    Promise.allSettled([
      apiCall(`${API_ENDPOINTS.SALES_V2_TREND}?${qs({ grain })}`)
        .then((r) => ({ label: 'Current', rows: (r && Array.isArray(r.data)) ? r.data : [] })),
      ...cmpPeriods.map((p) =>
        apiCall(`${API_ENDPOINTS.SALES_V2_TREND}?${qs({ grain, from: p.from, to: p.to })}`)
          .then((r) => ({ label: p.label, rows: (r && Array.isArray(r.data)) ? r.data : [] }))),
    ]).then((settled) => {
      if (!live) return;
      setGraphSeries({
        grain,
        series: settled.map((s, i) => (s.status === 'fulfilled'
          ? s.value
          : { label: i === 0 ? 'Current' : (cmpPeriods[i - 1]?.label || `P${i}`), rows: [] })),
      });
    });
    return () => { live = false; };
  }, [viewMode, graphMode, qs, cmpKey, rangeDays]); // cmpPeriods participates via cmpKey

  // Graph 'top' mode: one revenue series per top-N entity of this dimension,
  // from a single two-pass ClickHouse query (top-N by revenue, then their
  // per-day/month series). Respects the tab's active search + global filters.
  useEffect(() => {
    if (viewMode !== 'graph' || graphMode !== 'top' || !SERIES_DIMS.has(tabKey)) return undefined;
    let live = true;
    const grain = (Number.isFinite(rangeDays) && rangeDays > 190) ? 'month' : 'day';
    setEntSeries(null);
    apiCall(`${API_ENDPOINTS.SALES_V2_REPORT_SERIES}?${qs({ dim: tabKey, grain, top: graphTop, ...(q ? { q } : {}) })}`)
      .then((r) => { if (live) setEntSeries({ grain, rows: (r && Array.isArray(r.data)) ? r.data : [] }); })
      .catch(() => { if (live) setEntSeries({ grain, rows: [] }); });
    return () => { live = false; };
  }, [viewMode, graphMode, graphTop, tabKey, qs, q, rangeDays]);

  // rows grouped into per-entity lines, entities ordered by window revenue
  const entData = useMemo(() => {
    if (!entSeries) return { list: [], rows: [] };
    const totals = new Map();
    entSeries.rows.forEach((r) => totals.set(r.x, (totals.get(r.x) || 0) + (Number(r.revenue) || 0)));
    const list = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([x]) => x);
    const idx = new Map(list.map((x, i) => [x, i]));
    const byP = new Map();
    entSeries.rows.forEach((r) => {
      const key = String(r.period);
      if (!byP.has(key)) byP.set(key, { period: r.period });
      byP.get(key)[`e${idx.get(r.x)}`] = Number(r.revenue) || 0;
    });
    return { list, rows: [...byP.values()].sort((a, b) => String(a.period).localeCompare(String(b.period))) };
  }, [entSeries]);

  // Aligned by DATE OFFSET from each window's start (not array index — the
  // trend endpoint skips zero-sale days, and index pairing would shift sparse
  // filtered series onto the wrong dates).
  const graphData = useMemo(() => {
    if (!graphSeries) return [];
    const grain = graphSeries.grain;
    const off = (p, start) => {
      if (!p || !start) return -1;
      const a = new Date(String(p).slice(0, 10)); const b = new Date(String(start).slice(0, 10));
      return grain === 'day'
        ? Math.round((a - b) / 86400000)
        : (a.getUTCFullYear() - b.getUTCFullYear()) * 12 + (a.getUTCMonth() - b.getUTCMonth());
    };
    const starts = [range?.from, ...cmpPeriods.map((p) => p.from)];
    const rowsByOff = new Map();
    graphSeries.series.forEach((s, k) => {
      s.rows.forEach((r) => {
        const o = off(r.period, starts[k]);
        if (o < 0) return;
        if (!rowsByOff.has(o)) rowsByOff.set(o, { i: o });
        const row = rowsByOff.get(o);
        row[`v${k}`] = Number(r.revenue) || 0; row[`d${k}`] = r.period;
      });
    });
    return [...rowsByOff.values()].sort((a, b) => a.i - b.i);
  }, [graphSeries, range, cmpPeriods]);

  // debounce search → server-side q
  useEffect(() => {
    const t = setTimeout(() => setQ(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // main report fetch — one ClickHouse aggregation per tab + filters
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setError('');
    apiCall(`${API_ENDPOINTS.SALES_V2_REPORT}?${qs({ dim: tabKey, limit: 5000, ...(tabKey === 'sku' || tabKey === 'collection' ? { thumbs: 1 } : {}), ...(cmpKey ? { periods: cmpKey } : compare ? { compare: 1 } : {}), ...(q ? { q } : {}) })}`, { signal: ac.signal })
      .then((r) => {
        if (abortRef.current !== ac) return;
        setRows((r && r.data) || []);
        setTotals((r && r.totals) || null);
        setLoading(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        if (abortRef.current !== ac) return;
        setError(e.message || 'Failed to load report');
        setLoading(false);
      });
    return () => ac.abort();
  }, [tabKey, qs, compare, q, cmpKey]);

  // state-level data for the zip tab's heat map. The global state filter is
  // deliberately excluded (state:'' overrides it) so the map always shows every
  // state — otherwise drilling into one state made all others unclickable.
  useEffect(() => {
    if (!(tabKey === 'zip' && viewMode === 'map')) return undefined;
    let live = true;
    apiCall(`${API_ENDPOINTS.SALES_V2_REPORT}?${qs({ dim: 'state', limit: 60, state: '' })}`)
      .then((r) => { if (live) setStateRows((r && r.data) || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [tabKey, viewMode, qs]);

  // warehouse map: ship-to footprint by state — the whole network by default,
  // or the focused warehouse's when one is selected. The global warehouse and
  // state filters are overridden so the footprint always covers the full map.
  useEffect(() => {
    if (!(tabKey === 'warehouse' && viewMode === 'map')) return undefined;
    let live = true;
    apiCall(`${API_ENDPOINTS.SALES_V2_REPORT}?${qs({ dim: 'state', limit: 60, state: '', warehouse: lastSel || '' })}`)
      .then((r) => { if (live) setWhStates((r && r.data) || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [tabKey, viewMode, qs, lastSel]);

  // ---- sorted + limited rows -------------------------------------------------
  const displayed = useMemo(() => {
    let out = [...rows];
    if (sortCol >= 0 && columns[sortCol] && columns[sortCol].raw) {
      const primary = columns[sortCol].raw;
      const secondaryCol = columns.findIndex((c, i) => c.sortable && i !== sortCol);
      const secondary = secondaryCol >= 0 ? columns[secondaryCol].raw : null;
      out.sort((a, b) => {
        const d = sortDir === 'asc' ? primary(a) - primary(b) : primary(b) - primary(a);
        if (d !== 0 || !secondary) return d;
        return sortDir === 'asc' ? secondary(a) - secondary(b) : secondary(b) - secondary(a);
      });
    }
    if (topN > 0) out = out.slice(0, topN);
    return out;
  }, [rows, sortCol, sortDir, topN, columns]);

  const chartData = useMemo(() => displayed.slice(0, 25).map((r) => {
    const label = String(columns[0].cell(r));
    return {
      name: label.length > 20 ? label.slice(0, 18) + '…' : label, fullName: label,
      Amount: Number(r.revenue) || 0,
      fill: tabKey === 'color' ? colorFill(r.x) : null,
    };
  }), [displayed, columns, tabKey]);

  const pieData = useMemo(() => {
    const items = displayed.slice(0, 10).map((r) => {
      const label = String(columns[0].cell(r));
      return {
        name: label.length > 25 ? label.slice(0, 23) + '…' : label, fullName: label,
        value: Number(r.revenue) || 0,
        fill: tabKey === 'color' ? colorFill(r.x) : null,
      };
    });
    const total = items.reduce((s, i) => s + i.value, 0);
    return items.map((i) => ({ ...i, pct: total > 0 ? ((i.value / total) * 100).toFixed(1) : '0.0' }));
  }, [displayed, columns, tabKey]);

  // ---- DS vs Non-DS bar --------------------------------------------------------
  const dsBar = useMemo(() => {
    const find = (name) => Number((dropship || []).find((d) => d.source === name)?.revenue) || 0;
    const ds = find('Drop-ship'); const non = find('Non-drop-ship');
    const total = ds + non;
    return total > 0 ? { ds, non, total, dsPct: ((ds / total) * 100).toFixed(1), nonPct: ((non / total) * 100).toFixed(1) } : null;
  }, [dropship]);

  // ---- exports -----------------------------------------------------------------
  const buildAoa = useCallback(() => {
    const cols = columns.filter((c) => !c.noExport); // swatch chip is display-only
    const head = ['#', ...cols.map((c) => c.label)];
    const body = displayed.map((r, i) => [i + 1, ...cols.map((c) => String(c.cell(r)))]);
    return [head, ...body];
  }, [columns, displayed]);

  const handleCopy = useCallback(() => {
    const csv = buildAoa().map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    navigator.clipboard.writeText(csv).catch(() => {});
    setExportAnchor(null);
  }, [buildAoa]);

  const handleExportCSV = useCallback(() => {
    const csv = buildAoa().map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tabDef.file}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  }, [buildAoa, tabDef.file]);

  const handleExportExcel = useCallback(async () => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildAoa()), tabDef.title.slice(0, 31));
      // Top Customers gets a second sheet: SKU details per displayed customer.
      // The active search (q) is forwarded so the detail set matches the screen.
      if (tabKey === 'customer' && displayed.length > 0) {
        const custLimit = topN > 0 ? Math.min(Math.max(topN, displayed.length), 500) : 500;
        const detail = await apiCall(`${API_ENDPOINTS.SALES_V2_CUSTOMER_ITEM_DETAILS}?${qs({ customers: custLimit, ...(q ? { q } : {}) })}`);
        const shown = new Set(displayed.map((r) => r.x));
        const detailRows = [['Customer', 'SKU', 'Collection', 'Color', 'Size', 'Style', 'Units', 'Amount']];
        ((detail && detail.data) || []).filter((d) => shown.has(d.customer)).forEach((d) => {
          detailRows.push([d.customer, d.item, d.collection || '', d.color || '', d.size || '', d.style || '', Number(d.units) || 0, Number(d.revenue) || 0]);
        });
        if (detailRows.length === 1) detailRows.push(['(no matching detail rows — try a smaller Top N or clear the search)', '', '', '', '', '', '', '']);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(detailRows), 'SKU Details by Customer');
      }
      XLSX.writeFile(wb, `${tabDef.file}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [buildAoa, tabDef, tabKey, displayed, topN, qs, q]);

  // ---- map helpers ---------------------------------------------------------------
  // Every warehouse in the report gets a pin: curated (overlap-nudged) coords
  // first, then real lat/lng from the warehouses table, projection-guarded so
  // an unprojectable point (outside the AlbersUSA insets) can never crash the map.
  const albers = useMemo(() => geoAlbersUsa(), []);
  const whMapData = useMemo(() => {
    if (tabKey !== 'warehouse') return [];
    const seen = new Map(); // "lat,lng" -> count, to fan out perfectly overlapping pins
    return rows.map((r) => {
      const code = r.x;
      const cur = WAREHOUSE_LOCATIONS[code];
      let lat = cur ? cur.lat : Number(r.lat);
      let lng = cur ? cur.lng : Number(r.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
      if (!albers([lng, lat])) return null; // outside the projection → table-only
      const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;
      const n = seen.get(key) || 0;
      seen.set(key, n + 1);
      if (n > 0) { lat += 0.55 * n; lng += 0.55 * n; } // nudge exact overlaps apart
      return {
        code, lat, lng,
        name: cur?.name || [r.label, r.wstate].filter(Boolean).join(', ') || code,
        region: cur?.region || 'Central',
        orders: Number(r.orders) || 0, units: Number(r.units) || 0, revenue: Number(r.revenue) || 0,
      };
    }).filter(Boolean).sort((a, b) => b.revenue - a.revenue);
  }, [tabKey, rows, albers]);

  const toggleWh = (code) => {
    setSelectedWh((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
        setLastSel((cur) => (cur === code ? ([...next][next.size - 1] || '') : cur));
      } else {
        next.add(code);
        setLastSel(code);
      }
      return next;
    });
  };

  const stateByAbbrev = useMemo(() => Object.fromEntries(stateRows.map((r) => [String(r.x || '').toUpperCase(), r])), [stateRows]);
  const maxStateRev = useMemo(() => Math.max(1, ...stateRows.map((r) => Number(r.revenue) || 0)), [stateRows]);
  const totalStateRev = useMemo(() => stateRows.reduce((s, r) => s + (Number(r.revenue) || 0), 0), [stateRows]);

  const hideTip = () => { setMapTooltip(null); setMapTooltipPos(null); };

  const changeColor = (text) => (text === 'NEW' ? theme.palette.info.main : text.startsWith('+') ? theme.palette.success.main : theme.palette.error.main);

  // =============================================================================
  return (
    <Box>
      {/* title + subtitle */}
      <Typography variant="h6" sx={{ fontSize: 18, color: 'primary.main', fontWeight: 700, mb: 0.25 }}>{tabDef.title}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>{SUBTITLES[tabKey] ? SUBTITLES[tabKey](ctx) : ''}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* summary cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 1, md: 2 }, mb: 2 }}>
        <SummaryCard label="Total Quantity" value={loading && !totals ? '…' : fmtNum(totals?.units)} icon={MdInventory2} theme={theme} />
        <SummaryCard label="Total Amount" value={loading && !totals ? '…' : fmtDollar(totals?.revenue)} icon={MdAttachMoney} theme={theme} />
        <SummaryCard label="Unique Entries" value={loading && !totals ? '…' : fmtNum(totals?.entries)} icon={MdFormatListBulleted} theme={theme} />
        <SummaryCard label="Showing" value={fmtNum(displayed.length)} icon={MdVisibility} theme={theme} />
      </Box>

      {/* drill indicator (zip tab) */}
      {stateFilter && tabKey === 'zip' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip label={`Drilling into: ${stateFilter}`} onDelete={() => onDrillState('')} sx={{ fontWeight: 600 }} color="primary" variant="outlined" />
          <Typography variant="caption" color="text.secondary">Click a state on the map to drill down, or clear to see all</Typography>
        </Box>
      )}

      {/* DS vs Non-DS bar */}
      {dsBar && (
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
            DS vs Non-DS (revenue share)
          </Typography>
          <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', height: 24 }}>
            {dsBar.ds > 0 && (
              <MuiTooltip title={`Drop-ship: ${fmtDollar(dsBar.ds)} (${dsBar.dsPct}%)`}>
                <Box sx={{ width: `${dsBar.dsPct}%`, bgcolor: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 40 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>DS {dsBar.dsPct}%</Typography>
                </Box>
              </MuiTooltip>
            )}
            {dsBar.non > 0 && (
              <MuiTooltip title={`Non-drop-ship: ${fmtDollar(dsBar.non)} (${dsBar.nonPct}%)`}>
                <Box sx={{ width: `${dsBar.nonPct}%`, bgcolor: '#6B8F71', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 40 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>Non-DS {dsBar.nonPct}%</Typography>
                </Box>
              </MuiTooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>DS: {fmtDollar(dsBar.ds)}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Non-DS: {fmtDollar(dsBar.non)}</Typography>
          </Box>
        </Paper>
      )}

      {/* toolbar: search + topN + view mode + exports */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search…" size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }}
          sx={{ width: 220 }} />
        <Select value={topN} onChange={(e) => setTopN(e.target.value)} size="small" sx={{ fontSize: 12, minWidth: 90 }}>
          {TOP_N_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>Top {o.label}</MenuItem>)}
        </Select>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
          <ToggleButton value="table" sx={{ px: 1.2, textTransform: 'none' }}><MdTableChart size={15} style={{ marginRight: 4 }} /> Table</ToggleButton>
          <ToggleButton value="chart" sx={{ px: 1.2, textTransform: 'none' }}><MdBarChart size={15} style={{ marginRight: 4 }} /> Chart</ToggleButton>
          <ToggleButton value="pie" sx={{ px: 1.2, textTransform: 'none' }}><MdPieChart size={15} style={{ marginRight: 4 }} /> Pie</ToggleButton>
          <ToggleButton value="graph" sx={{ px: 1.2, textTransform: 'none' }}><MdShowChart size={15} style={{ marginRight: 4 }} /> Graph</ToggleButton>
          {mapAllowed && <ToggleButton value="map" sx={{ px: 1.2, textTransform: 'none' }}><MdMap size={15} style={{ marginRight: 4 }} /> Map</ToggleButton>}
        </ToggleButtonGroup>
        <MuiTooltip title="Copy to clipboard">
          <IconButton size="small" onClick={handleCopy} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
            <MdContentCopy size={15} />
          </IconButton>
        </MuiTooltip>
        <Button size="small" variant="contained" startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <MdFileDownload />}
          endIcon={<MdArrowDropDown />} onClick={(e) => setExportAnchor(e.currentTarget)} disabled={exporting || displayed.length === 0}>
          Export
        </Button>
        <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={handleExportExcel} sx={{ fontSize: 13 }}>Export Excel{tabKey === 'customer' ? ' (+ SKU details sheet)' : ''}</MenuItem>
          <MenuItem onClick={handleExportCSV} sx={{ fontSize: 13 }}>Export CSV</MenuItem>
        </Menu>
      </Box>

      {/* ---- graph view: per-entity lines ('top') or total vs periods ('total') ---- */}
      {viewMode === 'graph' && (() => {
        const topOk = SERIES_DIMS.has(tabKey);
        const mode = topOk ? graphMode : 'total';
        const grain = (mode === 'top' ? entSeries?.grain : graphSeries?.grain) || 'day';
        const fmtTick = (v) => {
          if (!v) return '';
          const d = new Date(v);
          return grain === 'day'
            ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        };
        const dimName = (REPORT_TABS.find((t) => t.key === tabKey)?.label || 'entries').toLowerCase();
        // one chart node, rendered inline AND inside the fullscreen dialog —
        // ResponsiveContainer re-flows to whichever box it lands in
        const chartNode = mode === 'top' ? (
          !entSeries ? <Skeleton variant="rounded" height={360} />
            : entData.rows.length === 0 ? <Typography sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No data to graph</Typography> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={entData.rows} margin={{ top: 6, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="period" tickFormatter={fmtTick} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tickFormatter={fmtDollarShort} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
                <Tooltip
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12, boxShadow: theme.shadows[3] }}
                  labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  itemStyle={{ color: theme.palette.text.primary }}
                  labelFormatter={fmtTick}
                  formatter={(v, n) => [fmtDollar(v), n]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {entData.list.map((x, k) => (
                  <RLine key={x} type="monotone" dataKey={`e${k}`}
                    name={String(x).length > 24 ? String(x).slice(0, 22) + '…' : String(x)}
                    stroke={PERIOD_COLORS[k % PERIOD_COLORS.length]}
                    strokeWidth={k === 0 ? 2.4 : 1.7} dot={false} activeDot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )
        ) : (
          !graphSeries ? <Skeleton variant="rounded" height={360} />
            : graphData.length === 0 ? <Typography sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No data to graph</Typography> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graphData} margin={{ top: 6, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                <XAxis dataKey="d0" tickFormatter={fmtTick} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tickFormatter={fmtDollarShort} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
                <Tooltip
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12, boxShadow: theme.shadows[3] }}
                  labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  itemStyle={{ color: theme.palette.text.primary }}
                  labelFormatter={() => ''}
                  formatter={(v, n, entry) => {
                    const m = /^v(\d+)$/.exec(entry?.dataKey || '');
                    const d = m ? entry?.payload?.[`d${m[1]}`] : null;
                    return [fmtDollar(v), `${n}${d ? ` (${fmtTick(d)})` : ''}`];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <RLine type="monotone" dataKey="v0" name="Current" stroke={accent} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />
                {cmpPeriods.map((p, k) => (
                  <RLine key={`v${k + 1}`} type="monotone" dataKey={`v${k + 1}`} name={p.label}
                    stroke={PERIOD_COLORS[k % PERIOD_COLORS.length]} strokeWidth={1.8}
                    strokeDasharray="5 3" dot={false} activeDot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )
        );
        const fsTitle = mode === 'top'
          ? `${tabDef.title} — Top ${graphTop} ${dimName} over time`
          : `${tabDef.title} — Total revenue over time${cmpPeriods.length ? ` vs ${cmpPeriods.length} period${cmpPeriods.length > 1 ? 's' : ''}` : ''}`;
        return (<>
          <Paper elevation={0} sx={{ borderRadius: 2, p: { xs: 1.5, md: 2.5 }, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1, minWidth: 220 }}>
                {mode === 'top'
                  ? `Top ${graphTop} ${dimName} — revenue over time (${grain === 'day' ? 'daily' : 'monthly'}), within the active filters${q ? ' + search' : ''}`
                  : `Total revenue over time (${grain === 'day' ? 'daily' : 'monthly'}) within the active filters${cmpPeriods.length ? ` — vs ${cmpPeriods.length} comparison period${cmpPeriods.length > 1 ? 's' : ''}, aligned ${grain} 1 over ${grain} 1` : ' — add periods via Compare above to overlay past ranges'}`}
              </Typography>
              {mode === 'top' && (
                <Select size="small" value={graphTop} onChange={(e) => setGraphTop(e.target.value)} sx={{ fontSize: 12 }}>
                  {[3, 5, 8, 10].map((n) => <MenuItem key={n} value={n}>Top {n}</MenuItem>)}
                </Select>
              )}
              {topOk && (
                <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setGraphMode(v)}>
                  <ToggleButton value="top" sx={{ px: 1.2, textTransform: 'none', fontSize: 12 }}>Top {dimName}</ToggleButton>
                  <ToggleButton value="total" sx={{ px: 1.2, textTransform: 'none', fontSize: 12 }}>Total{cmpPeriods.length ? ' vs periods' : ''}</ToggleButton>
                </ToggleButtonGroup>
              )}
              <FsButton onClick={() => setFsView(true)} />
            </Box>
            <Box sx={{ height: 400 }}>{chartNode}</Box>
          </Paper>
          <ChartFullscreen open={fsView} onClose={() => setFsView(false)} title={fsTitle}>{chartNode}</ChartFullscreen>
        </>);
      })()}

      {/* ---- bar chart view ---- */}
      {viewMode === 'chart' && (() => {
        const chartNode = chartData.length === 0
          ? <Typography sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No data to chart</Typography>
          : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" barCategoryGap="22%" margin={{ top: 4, right: 68, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="v2barGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <CartesianGrid horizontal={false} stroke={theme.palette.divider} strokeDasharray="2 6" strokeOpacity={0.7} />
                <XAxis type="number" tickFormatter={fmtDollarShort} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" width={160} tick={{ fill: theme.palette.text.primary, fontSize: 11.5 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: alpha(accent, 0.08) }}
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12, boxShadow: theme.shadows[3] }}
                  labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  itemStyle={{ color: theme.palette.text.primary }}
                  formatter={(v) => [fmtDollar(v), 'Amount']}
                  labelFormatter={(l, payload) => payload?.[0]?.payload?.fullName || l}
                />
                <Bar dataKey="Amount" radius={[0, 6, 6, 0]} maxBarSize={28}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.fill || 'url(#v2barGrad)'} />)}
                  <LabelList dataKey="Amount" position="right" formatter={fmtDollarShort}
                    style={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          );
        return (<>
          <Paper elevation={0} sx={{ borderRadius: 2, p: { xs: 1.5, md: 2.5 }, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1 }}>
                Top {Math.min(25, displayed.length)} by Amount{tabKey === 'color' ? ' — bars tinted to each color' : ''}
              </Typography>
              <FsButton onClick={() => setFsView(true)} />
            </Box>
            <Box sx={{ height: Math.max(360, chartData.length * 30) }}>{chartNode}</Box>
          </Paper>
          <ChartFullscreen open={fsView} onClose={() => setFsView(false)} title={`${tabDef.title} — Top ${Math.min(25, displayed.length)} by Amount`}>{chartNode}</ChartFullscreen>
        </>);
      })()}

      {/* ---- pie view ---- */}
      {viewMode === 'pie' && (() => {
        const pieNode = (big) => pieData.length === 0
          ? <Typography sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No data to chart</Typography>
          : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={big ? '72%' : 145} innerRadius={big ? '30%' : 58} dataKey="value" paddingAngle={1}
                  stroke={theme.palette.background.paper} strokeWidth={2}
                  label={({ name, pct }) => `${name} (${pct}%)`}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.fill || PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                  itemStyle={{ color: theme.palette.text.primary }}
                  formatter={(v, n, p) => [`${fmtDollar(v)} (${p?.payload?.pct}%)`, p?.payload?.fullName]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          );
        return (<>
          <Paper elevation={0} sx={{ borderRadius: 2, p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1 }}>Top {Math.min(10, displayed.length)} by Amount</Typography>
              <FsButton onClick={() => setFsView(true)} />
            </Box>
            <Box sx={{ height: 430 }}>{pieNode(false)}</Box>
          </Paper>
          <ChartFullscreen open={fsView} onClose={() => setFsView(false)} title={`${tabDef.title} — Top ${Math.min(10, displayed.length)} by Amount`}>{pieNode(true)}</ChartFullscreen>
        </>);
      })()}

      {/* ---- warehouse map: footprint choropleth + flow arcs + scaled bubbles ---- */}
      {viewMode === 'map' && tabKey === 'warehouse' && (() => {
        const totalWhsRev = whMapData.reduce((s, w) => s + w.revenue, 0);
        const maxWhsRev = Math.max(1, ...whMapData.map((w) => w.revenue));
        const focused = lastSel ? whMapData.find((w) => w.code === lastSel) : null;
        const maxFpRev = Math.max(1, ...whStates.map((r) => Number(r.revenue) || 0));
        const totalFpRev = whStates.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
        const fpByState = Object.fromEntries(whStates.map((r) => [String(r.x || '').toUpperCase(), r]));
        // arcs: focused warehouse -> its top destination states
        const arcs = focused ? whStates.slice(0, 8).map((r) => {
          const st = String(r.x || '').toUpperCase();
          const coords = STATE_COORDS[st];
          if (!coords || !albers([coords[1], coords[0]])) return null;
          return { st, to: [coords[1], coords[0]], rev: Number(r.revenue) || 0 };
        }).filter(Boolean) : [];
        const maxArcRev = Math.max(1, ...arcs.map((a) => a.rev));
        const selected = whMapData.filter((w) => selectedWh.has(w.code));
        const maxSelRev = Math.max(1, ...selected.map((w) => w.revenue));
        const totalSel = selected.reduce((s, w) => ({ o: s.o + w.orders, u: s.u + w.units, r: s.r + w.revenue }), { o: 0, u: 0, r: 0 });
        return (
          <Paper elevation={0} sx={{ borderRadius: 2, p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {focused ? `${focused.code} — ${focused.name} · ship-to footprint` : 'Warehouse Network — ship-to revenue by state'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Bubble size = warehouse revenue · shading = destination revenue{focused ? ` from ${focused.code}` : ' (whole network)'} · click a warehouse to focus, click again to release
                </Typography>
              </Box>
              {selectedWh.size > 0 && (
                <Button size="small" onClick={() => { setSelectedWh(new Set()); setLastSel(''); }}
                  sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Clear selection ({selectedWh.size})</Button>
              )}
            </Box>
            <Box sx={{ bgcolor: dark ? '#141422' : '#eef3f8', borderRadius: 1.5, overflow: 'hidden', position: 'relative', mt: 1 }}>
              <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                <ZoomableGroup zoom={mapPos.zoom} center={mapPos.coordinates} minZoom={1} maxZoom={12}
                  onMoveEnd={(p) => setMapPos(p)} filterZoomEvent={wheelFilter}>
                  <Geographies geography={US_TOPO_URL}>
                    {({ geographies }) => geographies.map((geo) => {
                      const stName = geo.properties?.name || '';
                      const abbrev = STATE_NAME_TO_ABBREV[stName];
                      const fp = abbrev ? fpByState[abbrev] : null;
                      const rev = Number(fp?.revenue) || 0;
                      const intensity = rev > 0 ? Math.max(0.1, Math.min(0.8, rev / maxFpRev)) : 0;
                      const fill = rev > 0 ? `rgba(0, 114, 178, ${intensity})` : (dark ? '#232336' : '#dde5ee');
                      const pct = totalFpRev > 0 ? ((rev / totalFpRev) * 100).toFixed(1) : '0.0';
                      return (
                        <Geography key={geo.rsmKey || geo.id} geography={geo}
                          fill={fill} stroke={dark ? '#32324a' : '#c3cedb'} strokeWidth={0.5}
                          style={{ default: { outline: 'none' }, hover: { fill: dark ? '#3a3a5e' : '#c9d6e4', outline: 'none' }, pressed: { outline: 'none' } }}
                          onMouseEnter={(e) => {
                            setMapTooltip({
                              title: abbrev || stName,
                              subtitle: focused ? `Shipped from ${focused.code}` : 'All warehouses',
                              rows: fp ? [
                                { label: 'Amount', value: fmtDollar(fp.revenue), color: '#90EE90' },
                                { label: 'Qty', value: fmtNum(fp.units) },
                                { label: 'Orders', value: fmtNum(fp.orders) },
                                { label: '% of footprint', value: `${pct}%` },
                              ] : [{ label: 'No shipments', value: '' }],
                            });
                            setMapTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={hideTip} />
                      );
                    })}
                  </Geographies>
                  {/* flow arcs from the focused warehouse to its top destinations */}
                  {focused && arcs.map((a) => (
                    <Line key={a.st} from={[focused.lng, focused.lat]} to={a.to}
                      stroke={accent} strokeWidth={Math.max(1, (a.rev / maxArcRev) * 4)}
                      strokeOpacity={0.55} strokeLinecap="round" fill="none" />
                  ))}
                  {/* warehouse bubbles, sized by revenue */}
                  {[...whMapData].reverse().map(({ code, lat, lng, name, region, orders, units, revenue }) => {
                    const isSel = selectedWh.has(code);
                    const isFocus = code === lastSel;
                    const color = REGION_COLORS[region] || accent;
                    const r = 4 + Math.sqrt(revenue / maxWhsRev) * 14;
                    const pct = totalWhsRev > 0 ? ((revenue / totalWhsRev) * 100).toFixed(1) : '0.0';
                    return (
                      <Marker key={code} coordinates={[lng, lat]}>
                        <g style={{ cursor: 'pointer' }} onClick={() => toggleWh(code)}
                          onMouseEnter={(e) => {
                            setMapTooltip({ title: `${code} — ${name}`, subtitle: `Region: ${region}${isFocus ? ' · focused' : ''}`, rows: [
                              { label: 'Amount', value: fmtDollar(revenue), color: '#90EE90' },
                              { label: 'Qty', value: fmtNum(units) },
                              { label: 'Orders', value: fmtNum(orders) },
                              { label: '% of network', value: `${pct}%` },
                            ]});
                            setMapTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={hideTip}>
                          {isFocus && <circle r={r + 4} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="3 2" opacity={0.9} />}
                          <circle r={r} fill={color} fillOpacity={revenue > 0 ? 0.85 : 0.3}
                            stroke={isSel ? '#fff' : (dark ? '#141422' : '#fff')} strokeWidth={isSel ? 2.5 : 1.25} />
                          {r >= 9 && (
                            <text textAnchor="middle" y={3.5} style={{ fontSize: 8, fontWeight: 800, fill: '#fff', fontFamily: 'sans-serif', pointerEvents: 'none' }}>{code}</text>
                          )}
                        </g>
                        {r < 9 && (
                          <text textAnchor="middle" y={r + 9} style={{ fontSize: 6.5, fontWeight: 700, fill: dark ? '#ccc' : '#445', fontFamily: 'sans-serif', pointerEvents: 'none' }}>{code}</text>
                        )}
                      </Marker>
                    );
                  })}
                </ZoomableGroup>
              </ComposableMap>
              <MapTooltipBox data={mapTooltip} position={mapTooltipPos} />
              <MapZoomControls pos={mapPos} setPos={setMapPos} dark={dark} />
              {/* floating stats card for the focused warehouse */}
              {focused && (
                <Paper elevation={3} sx={{
                  position: 'absolute', top: 10, right: 10, p: 1.5, borderRadius: 2, width: 210,
                  bgcolor: dark ? 'rgba(22,22,36,0.92)' : 'rgba(255,255,255,0.94)', backdropFilter: 'blur(4px)',
                  border: `1px solid ${REGION_COLORS[focused.region] || accent}`,
                }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, color: REGION_COLORS[focused.region] || 'primary.main' }}>{focused.code}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mb: 0.75 }}>{focused.name}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtDollar(focused.revenue)}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mb: 0.75 }}>
                    {fmtNum(focused.orders)} orders · {fmtNum(focused.units)} units · {totalWhsRev > 0 ? ((focused.revenue / totalWhsRev) * 100).toFixed(1) : 0}% of network
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.25 }}>Top destinations</Typography>
                  {whStates.slice(0, 5).map((r2) => (
                    <Box key={r2.x} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 11 }}>{r2.x}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtDollar(r2.revenue)}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
            {/* legends */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5, justifyContent: 'center', alignItems: 'center' }}>
              {Object.entries(REGION_COLORS).filter(([k]) => k !== 'Central').map(([region, color]) => (
                <Box key={region} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                  <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>{region}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>Ship-to: low</Typography>
                <Box sx={{ width: 90, height: 8, borderRadius: 4, background: 'linear-gradient(to right, rgba(0,114,178,0.1), rgba(0,114,178,0.8))' }} />
                <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>high</Typography>
              </Box>
            </Box>
            {selected.length >= 2 && (
              <Paper elevation={0} sx={{ mt: 2, p: 2, border: `2px solid ${accent}`, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>Comparing {selected.length} Warehouses</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${Math.min(selected.length, 4)}, 1fr)` }, gap: { xs: 1, md: 2 }, mb: 2 }}>
                  {selected.map((w) => (
                    <Paper key={w.code} elevation={0} sx={{ p: 1.5, border: `1px solid ${REGION_COLORS[w.region] || accent}`, borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: REGION_COLORS[w.region] }}>{w.code}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10, mb: 1 }}>{w.name}</Typography>
                      <Box sx={{ width: '100%', height: 6, bgcolor: theme.palette.action.hover, borderRadius: 3, mb: 1 }}>
                        <Box sx={{ width: `${(w.revenue / maxSelRev) * 100}%`, height: '100%', bgcolor: REGION_COLORS[w.region], borderRadius: 3 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{fmtDollar(w.revenue)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{fmtNum(w.orders)} orders · {fmtNum(w.units)} units</Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'primary.main' }}>
                        {totalSel.r > 0 ? ((w.revenue / totalSel.r) * 100).toFixed(1) : 0}% of selected
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Chip label={`Total: ${fmtDollar(totalSel.r)}`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                  <Chip label={`${fmtNum(totalSel.o)} orders`} size="small" sx={{ fontSize: 11 }} />
                  <Chip label={`${fmtNum(totalSel.u)} units`} size="small" sx={{ fontSize: 11 }} />
                  <Chip label={`Avg order: ${fmtDollar(totalSel.o > 0 ? totalSel.r / totalSel.o : 0)}`} size="small" sx={{ fontSize: 11 }} />
                </Box>
              </Paper>
            )}
          </Paper>
        );
      })()}

      {/* ---- state heat map (zip tab): choropleth + drill card + zoom controls ---- */}
      {viewMode === 'map' && tabKey === 'zip' && (() => {
        const topZips = displayed.slice(0, 10);
        const totalOrders = stateRows.reduce((s, r) => s + (Number(r.orders) || 0), 0);
        const drilled = stateFilter ? stateByAbbrev[stateFilter] : null;
        return (
          <Paper elevation={0} sx={{ borderRadius: 2, p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {stateFilter ? `Ship-to Heat Map — drilled into ${stateFilter}` : 'Ship-to Heat Map by State'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
              Shading = revenue · bubble size = revenue · click a state to drill its zips, click again to release
            </Typography>
            <Box sx={{ bgcolor: dark ? '#141422' : '#eef3f8', borderRadius: 1.5, overflow: 'hidden', position: 'relative' }}>
              <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                <ZoomableGroup zoom={mapPos.zoom} center={mapPos.coordinates} minZoom={1} maxZoom={12}
                  onMoveEnd={(p) => setMapPos(p)} filterZoomEvent={wheelFilter}>
                  <Geographies geography={US_TOPO_URL}>
                    {({ geographies }) => geographies.map((geo) => {
                      const stName = geo.properties?.name || '';
                      const abbrev = STATE_NAME_TO_ABBREV[stName];
                      const sales = abbrev ? stateByAbbrev[abbrev] : null;
                      const rev = Number(sales?.revenue) || 0;
                      const isDrilled = abbrev && abbrev === stateFilter;
                      const intensity = Math.max(0.12, Math.min(0.85, rev / maxStateRev));
                      const fill = sales ? `rgba(0, 114, 178, ${intensity})` : (dark ? '#232336' : '#dde5ee');
                      const pct = sales && totalStateRev > 0 ? ((rev / totalStateRev) * 100).toFixed(1) : '0.0';
                      return (
                        <Geography key={geo.rsmKey || geo.id} geography={geo}
                          fill={fill}
                          stroke={isDrilled ? '#E69F00' : (dark ? '#32324a' : '#c3cedb')}
                          strokeWidth={isDrilled ? 2 : 0.5}
                          style={{ default: { outline: 'none' }, hover: { fill: accent, outline: 'none', cursor: 'pointer' }, pressed: { outline: 'none' } }}
                          onClick={() => { if (abbrev) onDrillState(stateFilter === abbrev ? '' : abbrev); }}
                          onMouseEnter={(e) => {
                            setMapTooltip({
                              title: abbrev || stName,
                              subtitle: isDrilled ? `${stName} — click to release` : `${stName} — click to drill down`,
                              rows: sales ? [
                                { label: 'Amount', value: fmtDollar(sales.revenue), color: '#90EE90' },
                                { label: 'Qty', value: fmtNum(sales.units) },
                                { label: 'Orders', value: fmtNum(sales.orders) },
                                { label: '% of Total', value: `${pct}%` },
                              ] : [{ label: 'No shipments', value: '' }],
                            });
                            setMapTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseMove={(e) => setMapTooltipPos({ x: e.clientX, y: e.clientY })}
                          onMouseLeave={hideTip} />
                      );
                    })}
                  </Geographies>
                  {stateRows.slice(0, 30).map((r) => {
                    const st = String(r.x || '').toUpperCase();
                    const coords = STATE_COORDS[st];
                    if (!coords || !albers([coords[1], coords[0]])) return null;
                    const size = Math.max(4, Math.min(18, Math.sqrt((Number(r.revenue) || 0) / maxStateRev) * 18));
                    return (
                      <Marker key={st} coordinates={[coords[1], coords[0]]}>
                        <circle r={size} fill={accent} fillOpacity={st === stateFilter ? 0.95 : 0.7}
                          stroke={st === stateFilter ? '#E69F00' : '#fff'} strokeWidth={st === stateFilter ? 2 : 1}
                          style={{ pointerEvents: 'none' }} />
                        {size >= 7 && (
                          <text textAnchor="middle" y={3} style={{ fontSize: 7, fontWeight: 700, fill: '#fff', fontFamily: 'sans-serif', pointerEvents: 'none' }}>{st}</text>
                        )}
                      </Marker>
                    );
                  })}
                </ZoomableGroup>
              </ComposableMap>
              <MapTooltipBox data={mapTooltip} position={mapTooltipPos} />
              <MapZoomControls pos={mapPos} setPos={setMapPos} dark={dark} />
              {/* floating card for the drilled state */}
              {stateFilter && (
                <Paper elevation={3} sx={{
                  position: 'absolute', top: 10, right: 10, p: 1.5, borderRadius: 2, width: 215,
                  bgcolor: dark ? 'rgba(22,22,36,0.92)' : 'rgba(255,255,255,0.94)', backdropFilter: 'blur(4px)',
                  border: '1px solid #E69F00',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#E69F00' }}>{stateFilter}</Typography>
                    <Chip label="clear" size="small" onClick={() => onDrillState('')}
                      sx={{ height: 18, fontSize: 9.5, cursor: 'pointer' }} />
                  </Box>
                  {drilled ? (
                    <>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{fmtDollar(drilled.revenue)}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mb: 0.75 }}>
                        {fmtNum(drilled.orders)} orders · {fmtNum(drilled.units)} units · {totalStateRev > 0 ? ((Number(drilled.revenue) / totalStateRev) * 100).toFixed(1) : 0}% of total
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, mb: 0.75 }}>No shipments in range</Typography>
                  )}
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.25 }}>Top zips in {stateFilter}</Typography>
                  {topZips.slice(0, 5).map((z) => (
                    <Box key={`${z.x}-${z.state}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 11 }}>{z.x}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtDollar(z.revenue)}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, justifyContent: 'center' }}>
              <Chip label={`${stateRows.length} states`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: 11 }} />
              <Chip label={`${fmtNum(totalOrders)} orders`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
              <Chip label={`${fmtDollar(totalStateRev)} total`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
              <Chip label={`${fmtDollar(totalOrders > 0 ? totalStateRev / totalOrders : 0)} avg order`} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>Low</Typography>
              <Box sx={{ width: 120, height: 8, borderRadius: 4, background: 'linear-gradient(to right, rgba(0,114,178,0.12), rgba(0,114,178,0.85))' }} />
              <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>High</Typography>
            </Box>
            {topZips.length > 0 && (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: 12, color: 'primary.main' }}>Top 10 Zip Codes by Sales</Typography>
                {/* fixed layout + matching header/body alignment so numbers sit under their labels */}
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { h: '#', a: 'left', w: '7%' },
                        { h: 'Zip', a: 'left', w: '17%' },
                        { h: 'State', a: 'left', w: '13%' },
                        { h: 'Orders', a: 'right', w: '17%' },
                        { h: 'Qty', a: 'right', w: '17%' },
                        { h: 'Amount', a: 'right', w: '29%' },
                      ].map((c) => (
                        <TableCell key={c.h} align={c.a} sx={{ bgcolor: accent, color: '#fff', fontWeight: 600, fontSize: 11, py: 0.5, width: c.w }}>{c.h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topZips.map((z, i) => (
                      <TableRow key={`${z.x}-${z.state}`} hover>
                        <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>{z.x}</TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{z.state}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(z.orders)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(z.units)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtDollar(z.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        );
      })()}

      {/* ---- table view ---- */}
      {viewMode === 'table' && (
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: accent, color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', py: '10px', px: '14px' }}>#</TableCell>
                  {columns.map((c, idx) => (
                    <TableCell key={idx} align={c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left'}
                      onClick={c.sortable ? () => {
                        if (sortCol === idx) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                        else { setSortCol(idx); setSortDir('desc'); }
                      } : undefined}
                      sx={{
                        bgcolor: accent, color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
                        py: '10px', px: '14px', cursor: c.sortable ? 'pointer' : 'default', userSelect: 'none',
                      }}>
                      {c.label}
                      {c.sortable && sortCol === idx && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      {c.sortable && sortCol !== idx && <span style={{ marginLeft: 4, opacity: 0.4 }}>{'⇅'}</span>}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rows.length === 0 ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={columns.length + 1}><Skeleton height={22} /></TableCell>
                    </TableRow>
                  ))
                ) : displayed.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>No data found</TableCell>
                  </TableRow>
                ) : (
                  displayed.map((r, idx) => (
                    <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: theme.palette.action.hover } }}>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 13, px: '14px', py: '8px' }}>{idx + 1}</TableCell>
                      {columns.map((c, ci) => {
                        const text = c.render ? null : String(c.cell(r));
                        return (
                          <TableCell key={ci} align={c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left'}
                            sx={{ fontSize: 13, px: '14px', py: '8px', fontVariantNumeric: 'tabular-nums',
                              ...(c.isChange ? { color: changeColor(text), fontWeight: 700 } : {}) }}>
                            {c.render ? c.render(r) : text}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
